import i18n from './index';

// Call only for product-owned copy. Names, notes, prices and record identifiers
// must not be passed through a translation lookup.
export function tr(source, values = {}) {
  return i18n.t(source, { ...values, ns: 'copy', keySeparator: false, nsSeparator: false,
    defaultValue: source, interpolation: { escapeValue: false } });
}
export function uiLocale() {
  return { it: 'it-IT', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' }[i18n.resolvedLanguage || i18n.language] || 'it-IT';
}

// A few shared option lists live outside React. Only explicitly selected
// product-copy leaves are localized; keys, route names and record values are
// untouched. Getters read the current language, so switching never freezes
// these labels in the language active when the module was first imported.
export function localizedStatic(value, paths) {
  for (const path of paths) {
    const owner = path.slice(0, -1).reduce((node, key) => node[key], value);
    const key = path[path.length - 1];
    const source = owner[key];
    Object.defineProperty(owner, key, {
      enumerable: true, configurable: true,
      get: () => tr(source),
    });
  }
  return value;
}

const systemIndexes = new WeakMap();
const marker = /{{\s*([\w.]+)\s*}}/g;
const escapePattern = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
function systemIndex(resource) {
  if (systemIndexes.has(resource)) return systemIndexes.get(resource);
  const exact = new Map(), patterns = [];
  for (const [source, value] of Object.entries(resource)) {
    if (typeof value !== 'string') continue;
    for (const text of new Set([source, value])) {
      const slots = [...text.matchAll(marker)];
      if (!slots.length) { if (!exact.has(text)) exact.set(text, source); continue; }
      const literalSize = text.replace(marker, '').trim().length;
      if (literalSize < 8 || slots.length > 8 || slots.some((slot, i) => i && !text.slice(slots[i-1].index + slots[i-1][0].length, slot.index).trim())) continue;
      let cursor = 0, pattern = '';
      for (const slot of slots) { pattern += escapePattern(text.slice(cursor, slot.index)) + '([\\s\\S]{0,4096}?)'; cursor = slot.index + slot[0].length; }
      pattern += escapePattern(text.slice(cursor));
      patterns.push({source, pattern: new RegExp('^' + pattern + '$'), slots: slots.map(slot => slot[1]), literalSize});
    }
  }
  patterns.sort((a,b) => b.literalSize-a.literalSize);
  const result = {exact, patterns}; systemIndexes.set(resource, result); return result;
}

// Only server-owned labels/errors. Never pass customer names, notes, campaign
// text or signed snapshots here. Cached error text can follow a language change
// while preserving the interpolated record values and the user's open draft.
export function systemText(value) {
  if (typeof value !== 'string' || !value || value.length > 16384) return value;
  const loaded = Object.values(i18n.store?.data || {}).map(data => data.copy).filter(Boolean);
  for (const resource of loaded) if (Object.hasOwn(resource, value)) return tr(value);
  for (const resource of loaded) {
    const source = systemIndex(resource).exact.get(value);
    if (source) return tr(source);
  }
  for (const resource of loaded) for (const entry of systemIndex(resource).patterns) {
    const match = entry.pattern.exec(value);
    if (match) return tr(entry.source, Object.fromEntries(entry.slots.map((key,index) => [key,match[index+1]])));
  }
  return value;
}

export function notificationText(item, field) {
  const language = i18n.resolvedLanguage || i18n.language || 'it';
  return item?.display_copy?.[language]?.[field] ?? item?.[field] ?? '';
}
